import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreateLocation, Location, UpdateLocation } from '@thro-types/leihbar';

import { createLocation, deleteLocation, getLocations, updateLocation } from 'api';
import { AxiosRequestConfig } from 'axios';

/**
 * A custom hook for locations
 *
 * @returns DefinedUseQueryResult<Location[], Error>
 */
export const useLocationsQuery = (config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['locations'],
    queryFn: () => {
      return getLocations(config);
    }
  });

  return query;
};

export const useLocationMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();
  const queryKey = ['locations'];

  const create = useMutation<Location, Error, CreateLocation>({
    mutationFn: async args => {
      return await createLocation(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  const update = useMutation<Location, Error, { id: number; data: UpdateLocation }>({
    mutationFn: async args => {
      return await updateLocation(args.id, args.data, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  const deletion = useMutation<Location, Error, number>({
    mutationFn: async args => {
      return await deleteLocation(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  return { create, update, deletion } as const;
};
